import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Lock, Phone, Eye, EyeOff, CheckSquare, Square, User } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { adminUserService } from '../../services/adminUserService';
import { customToast } from '../../utils/customToast';
import { AdminUser, UpdateAdminUserRequest, AvailableSuperAdminModule, AdminUserPermission } from '../../types';

interface EditAdminUserModalProps {
    user: AdminUser;
    onClose: () => void;
    onSuccess: () => void;
}

const EditAdminUserModal: React.FC<EditAdminUserModalProps> = ({ user, onClose, onSuccess }) => {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [availableModules, setAvailableModules] = useState<AvailableSuperAdminModule[]>([]);
    const [loadingModules, setLoadingModules] = useState(true);

    const [formData, setFormData] = useState({
        full_name: user.full_name,
        phone: user.phone || '',
        password: '',
        is_active: user.is_active,
    });

    const [permissions, setPermissions] = useState<Record<string, {
        can_view: boolean;
        can_create: boolean;
        can_edit: boolean;
        can_delete: boolean;
    }>>({});

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        loadModules();
    }, []);

    const loadModules = async () => {
        try {
            setLoadingModules(true);
            const modules = await adminUserService.getAvailableModules();
            setAvailableModules(modules);

            // Initialize permissions from user's current permissions
            const initialPermissions: Record<string, any> = {};
            modules.forEach(mod => {
                const existingPerm = user.permissions.find(p => p.module === mod.value);
                initialPermissions[mod.value] = {
                    can_view: existingPerm?.can_view || false,
                    can_create: existingPerm?.can_create || false,
                    can_edit: existingPerm?.can_edit || false,
                    can_delete: existingPerm?.can_delete || false,
                };
            });
            setPermissions(initialPermissions);
        } catch (error) {
            console.error('Failed to load modules:', error);
            customToast.error('Failed to load permission modules');
        } finally {
            setLoadingModules(false);
        }
    };

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handlePermissionChange = (module: string, permission: string, value: boolean) => {
        setPermissions(prev => ({
            ...prev,
            [module]: {
                ...prev[module],
                [permission]: value,
                // If enabling create/edit/delete, also enable view
                ...(permission !== 'can_view' && value ? { can_view: true } : {}),
                // If disabling view, disable all others
                ...(permission === 'can_view' && !value ? { can_create: false, can_edit: false, can_delete: false } : {}),
            },
        }));
    };

    const handleSelectAllForModule = (module: string, allSelected: boolean) => {
        setPermissions(prev => ({
            ...prev,
            [module]: {
                can_view: !allSelected,
                can_create: !allSelected,
                can_edit: !allSelected,
                can_delete: !allSelected,
            },
        }));
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';

        if (formData.password && formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        // Check if at least one permission is granted
        const hasPermission = Object.values(permissions).some(p => p.can_view);
        if (!hasPermission) newErrors.permissions = 'Please grant at least one module access';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            // Update basic info
            const updateData: UpdateAdminUserRequest = {
                full_name: formData.full_name,
                phone: formData.phone || undefined,
                is_active: formData.is_active,
            };

            if (formData.password) {
                updateData.password = formData.password;
            }

            await adminUserService.updateAdminUser(user.id, updateData);

            // Update permissions
            const permissionsArray: AdminUserPermission[] = Object.entries(permissions)
                .filter(([_, p]) => p.can_view || p.can_create || p.can_edit || p.can_delete)
                .map(([module, p]) => ({
                    module,
                    can_view: p.can_view,
                    can_create: p.can_create,
                    can_edit: p.can_edit,
                    can_delete: p.can_delete,
                }));

            await adminUserService.updatePermissions(user.id, permissionsArray);

            customToast.success('Admin user updated successfully');
            onSuccess();
            handleClose();
        } catch (error: any) {
            console.error('Error updating admin user:', error);
            const message = error.response?.data?.message || 'Failed to update admin user';
            customToast.error(message);

            // Handle validation errors from backend
            if (error.response?.data?.errors) {
                const backendErrors: Record<string, string> = {};
                Object.entries(error.response.data.errors).forEach(([key, msgs]: [string, any]) => {
                    backendErrors[key] = Array.isArray(msgs) ? msgs[0] : msgs;
                });
                setErrors(backendErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setErrors({});
            onClose();
        }, 150);
    };

    return createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}>
            <div className="flex items-center justify-center min-h-screen px-4 py-6 sm:p-0">
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                    onClick={handleClose}
                />

                <div className={`inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-bottom sm:align-middle transform shadow-2xl rounded-lg ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
                    } ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
                    {/* Header */}
                    <div className={`px-4 sm:px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600">
                                    <User className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        Edit Admin User
                                    </h2>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {user.username} - {user.email}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className={`p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}`}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="px-4 sm:px-6 py-4 max-h-[60vh] overflow-y-auto">
                            {/* Basic Info */}
                            <div className="space-y-4 mb-6">
                                <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Basic Information
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Full Name */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.full_name}
                                            onChange={(e) => handleInputChange('full_name', e.target.value)}
                                            className={`w-full px-4 py-2 rounded-lg border ${errors.full_name ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                            placeholder="John Doe"
                                            disabled={user.is_original_admin}
                                        />
                                        {errors.full_name && <p className="mt-1 text-sm text-red-500">{errors.full_name}</p>}
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Phone
                                        </label>
                                        <div className="relative">
                                            <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                            <input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                placeholder="+92 300 1234567"
                                                disabled={user.is_original_admin}
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div className="sm:col-span-2">
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Change Password (optional)
                                        </label>
                                        <div className="relative">
                                            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={(e) => handleInputChange('password', e.target.value)}
                                                className={`w-full pl-10 pr-10 py-2 rounded-lg border ${errors.password ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                placeholder="Leave blank to keep current password"
                                                disabled={user.is_original_admin}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                                    </div>

                                    {/* Active Status */}
                                    <div className="sm:col-span-2">
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active}
                                                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                                disabled={user.is_original_admin}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                                Active (user can login)
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Permissions */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Module Permissions
                                    </h3>
                                    {errors.permissions && <p className="text-sm text-red-500">{errors.permissions}</p>}
                                </div>

                                {loadingModules ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : (
                                    <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                        <table className="w-full">
                                            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                                                <tr>
                                                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Module</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>View</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Create</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Edit</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Delete</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>All</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                                {availableModules.map((mod) => {
                                                    const perm = permissions[mod.value] || { can_view: false, can_create: false, can_edit: false, can_delete: false };
                                                    const allSelected = perm.can_view && perm.can_create && perm.can_edit && perm.can_delete;

                                                    return (
                                                        <tr key={mod.value} className={theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}>
                                                            <td className={`px-4 py-3 text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                <div>
                                                                    <div className="font-medium">{mod.label}</div>
                                                                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{mod.description}</div>
                                                                </div>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.value, 'can_view', !perm.can_view)}
                                                                    disabled={user.is_original_admin}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                                                                >
                                                                    {perm.can_view ? (
                                                                        <CheckSquare size={20} className="text-blue-600 dark:text-blue-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.value, 'can_create', !perm.can_create)}
                                                                    disabled={user.is_original_admin}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                                                                >
                                                                    {perm.can_create ? (
                                                                        <CheckSquare size={20} className="text-green-600 dark:text-green-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.value, 'can_edit', !perm.can_edit)}
                                                                    disabled={user.is_original_admin}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                                                                >
                                                                    {perm.can_edit ? (
                                                                        <CheckSquare size={20} className="text-amber-600 dark:text-amber-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.value, 'can_delete', !perm.can_delete)}
                                                                    disabled={user.is_original_admin}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                                                                >
                                                                    {perm.can_delete ? (
                                                                        <CheckSquare size={20} className="text-red-600 dark:text-red-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectAllForModule(mod.value, allSelected)}
                                                                    disabled={user.is_original_admin}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                                                                >
                                                                    {allSelected ? (
                                                                        <CheckSquare size={20} className="text-purple-600 dark:text-purple-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {user.is_original_admin && (
                                    <p className={`text-sm ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'} bg-yellow-100 dark:bg-yellow-900/20 p-3 rounded-lg`}>
                                        The original super admin account cannot be modified. This user has full access to all modules.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={`px-4 sm:px-6 py-4 border-t flex justify-between ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${theme === 'dark'
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || loadingModules || user.is_original_admin}
                                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                <Save size={16} />
                                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EditAdminUserModal;
